import React, { useCallback } from 'react';
import classNames from 'classnames';
import { changeUrl } from '$util/index';
import type { SchemaType } from '$util/schema';
import { CmsLinkSchema, CmsRichTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Button } from '@/components/atoms/Button';
import { SideBySideMediaBaseSchema } from '../SideBySideMediaBase/schema';
import { SideBySideMediaBase } from '../SideBySideMediaBase';
import styles from './index.module.scss';

export const SideBySideMediaTextSchema = {
  translatedEntities: SideBySideMediaBaseSchema.translatedEntities.extend({
    bodyCopy: CmsRichTextSchema,
    cta: CmsLinkSchema.optional(),
  }),
  configuration: SideBySideMediaBaseSchema.configuration,
};

export type SideBySideMediaTextTranslatedEntities = SchemaType<
  (typeof SideBySideMediaTextSchema)['translatedEntities']
>;
export type SideBySideMediaTextConfiguration = SchemaType<
  (typeof SideBySideMediaTextSchema)['configuration']
>;

/**
 * Component that renders Media and Text columns.<br />
 * https://www.notion.so/fixter/SideBySideMediaText-63c42ab94c314f2388169100635d62c6
 */
export const SideBySideMediaText: ContentComponent<
  SideBySideMediaTextTranslatedEntities,
  SideBySideMediaTextConfiguration
> = function SideBySideMediaText({ translatedEntities, configuration, className, ...props }) {
  const { bodyCopy, cta, ...translatedEntitiesBase } = translatedEntities;

  const onCtaClick = useCallback(() => {
    if (!cta) return;
    changeUrl({ pathname: cta.url });
  }, [cta]);

  return (
    <SideBySideMediaBase
      className={classNames(SideBySideMediaText.displayName, styles.container, className)}
      translatedEntities={translatedEntitiesBase}
      configuration={configuration}
      {...props}
    >
      <div className="body-copy">{renderRichTextContent(bodyCopy.content)}</div>
      {cta && <Button onClick={onCtaClick}>{cta.text}</Button>}
    </SideBySideMediaBase>
  );
};

SideBySideMediaText.displayName = 'SideBySideMediaText';
SideBySideMediaText.schema = SideBySideMediaTextSchema;
